import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { setBlockType } from "@/components/tiptap/utils/editorActions";
import { type DocumentModel } from "@/models/DocumentModel";

import { CollapsedPopover } from "../CollapsedPopover";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
  disabled?: boolean;
};

export const TextHeadingDropdown = observer(function TextHeadingDropdown({
  doc,
  isCollapsed,
  disabled,
}: Props) {
  const t = useTranslations("sidebar");
  const { tiptap, editorState } = doc.editor;

  if (!tiptap) {
    return null;
  }

  const items = [
    {
      title: t("textStyle.paragraph"),
      testId: "text-block-type",
      active: editorState.isParagraph,
      onClick: () => setBlockType(doc, "paragraph"),
      icon: <Icon name="block-format/Text" />,
    },
    {
      title: t("createBlocks.heading1.title"),
      caption: t("createBlocks.heading1.caption"),
      testId: "heading1-block-type",
      active: editorState.isH1,
      onClick: () => setBlockType(doc, "heading1"),
      icon: <Icon name="block-format/Heading1" />,
    },
    {
      title: t("createBlocks.heading2.title"),
      caption: t("createBlocks.heading2.caption"),
      testId: "heading2-block-type",
      active: editorState.isH2,
      onClick: () => setBlockType(doc, "heading2"),
      icon: <Icon name="block-format/Heading2" />,
    },
    {
      title: t("createBlocks.heading3.title"),
      caption: t("createBlocks.heading3.caption"),
      testId: "heading3-block-type",
      active: editorState.isH3,
      onClick: () => setBlockType(doc, "heading3"),
      icon: <Icon name="block-format/Heading3" />,
    },
  ] satisfies DropdownItem[];

  const getTooltipTitle = () => {
    if (editorState.isHeading) return t("createBlocks.heading.title");
    return t("textStyle.paragraph");
  };

  const activeIcon = items.find(({ active }) => active)?.icon || <Icon name="block-format/Text" />;

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={activeIcon}
        tooltipTitle={getTooltipTitle()}
        isActive={editorState.isParagraph || editorState.isHeading}
        items={items}
        itemsRole="radio"
      />
    );
  }

  return (
    <Dropdown
      items={items}
      itemsRole="menuitemradio"
      disabled={disabled}
      toggleButtonComponent={
        <Sidebar.ToggleButton
          as="div"
          tooltipTitle={getTooltipTitle()}
          isActive={editorState.isParagraph || editorState.isHeading}
          icon={activeIcon}
          disabled={disabled}
        />
      }
    />
  );
});
